import { useContext } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, MessageCircle } from "lucide-react";
import { AppContext } from "../App";
import { useLanguage } from "../contexts/LanguageContext";

const Footer = () => {
  const { settings } = useContext(AppContext);
  const { language, t } = useLanguage();

  const whatsappNumber = settings?.whatsapp?.replace(/\s/g, "") || "+436641234567";
  const whatsappLink = `https://wa.me/${whatsappNumber.replace("+", "")}`;

  // Footer description with language support
  const footerDescription = t(settings, "footer_description", 
    language === "de" 
      ? "Exklusive VIP-Begleitung in Wien. Diskretion und Eleganz seit 2020." 
      : "Exclusive VIP escort in Vienna. Discretion, elegance and unforgettable moments."
  );
  
  const footerCopyright = t(settings, "footer_copyright",
    language === "de"
      ? `© ${new Date().getFullYear()} Monaco Bar. Alle Rechte vorbehalten.`
      : `© ${new Date().getFullYear()} Monaco Bar. All rights reserved.`
  );

  const openingHours = settings?.opening_hours || {
    monday: { open: "18:00", close: "04:00", is_open: true },
    tuesday: { open: "18:00", close: "04:00", is_open: true },
    wednesday: { open: "18:00", close: "04:00", is_open: true },
    thursday: { open: "18:00", close: "04:00", is_open: true },
    friday: { open: "18:00", close: "04:00", is_open: true },
    saturday: { open: "18:00", close: "04:00", is_open: true },
    sunday: { open: "18:00", close: "04:00", is_open: true },
  };

  const dayNames = language === "de" ? {
    monday: "Montag",
    tuesday: "Dienstag",
    wednesday: "Mittwoch",
    thursday: "Donnerstag",
    friday: "Freitag",
    saturday: "Samstag",
    sunday: "Sonntag",
  } : {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  return (
    <footer className="bg-monaco-bg-1 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <h3 className="font-display text-2xl italic text-monaco-text-0 mb-4">
              Monaco Bar
            </h3>
            <p className="text-monaco-text-1 text-sm mb-6 leading-relaxed">
              {footerDescription}
            </p>
            <div className="flex gap-3">
              <a 
                href="#" 
                className="w-10 h-10 rounded-lg bg-monaco-bg-2 flex items-center justify-center text-monaco-text-1 hover:text-monaco-red hover:bg-monaco-red/10 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-monaco-bg-2 flex items-center justify-center text-monaco-text-1 hover:text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-monaco-text-0 font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-monaco-red" />
              {language === "de" ? "Öffnungszeiten" : "Opening Hours"}
            </h4>
            <ul className="space-y-2 text-sm">
              {Object.entries(openingHours).map(([day, hours]) => (
                <li key={day} className="flex justify-between">
                  <span className="text-monaco-text-1">{dayNames[day]}</span>
                  <span className={hours.is_open ? "text-monaco-text-0" : "text-monaco-text-2"}>
                    {hours.is_open ? `${hours.open} - ${hours.close}` : (language === "de" ? "Geschlossen" : "Closed")}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-monaco-text-0 font-semibold mb-4">{language === "de" ? "Kontakt" : "Contact"}</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(settings?.address || "Vienna")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-monaco-text-1 hover:text-monaco-text-0 transition-colors"
                >
                  <MapPin className="w-4 h-4 mt-0.5 text-monaco-red flex-shrink-0" />
                  <span>{settings?.address || "Musterstraße 1, 1010 Vienna"}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${settings?.phone || "+4312345678"}`}
                  className="flex items-center gap-3 text-monaco-text-1 hover:text-monaco-text-0 transition-colors"
                >
                  <Phone className="w-4 h-4 text-monaco-red" />
                  <span>{settings?.phone || "+43 1 234 5678"}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${settings?.email || "info@monaco-bar.at"}`}
                  className="flex items-center gap-3 text-monaco-text-1 hover:text-monaco-text-0 transition-colors"
                >
                  <Mail className="w-4 h-4 text-monaco-red" />
                  <span>{settings?.email || "info@monaco-bar.at"}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-monaco-text-0 font-semibold mb-4">Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/girls" className="text-monaco-text-1 hover:text-monaco-text-0 transition-colors">
                  Girls
                </Link>
              </li>
              <li>
                <Link to="/buchen" className="text-monaco-text-1 hover:text-monaco-text-0 transition-colors">
                  {language === "de" ? "Buchen" : "Booking"}
                </Link>
              </li>
              <li>
                <Link to="/jobs" className="text-monaco-text-1 hover:text-monaco-text-0 transition-colors">
                  Jobs
                </Link>
              </li>
              <li>
                <Link to="/impressum" className="text-monaco-text-1 hover:text-monaco-text-0 transition-colors">
                  {language === "de" ? "Impressum" : "Imprint"}
                </Link>
              </li>
              <li>
                <Link to="/datenschutz" className="text-monaco-text-1 hover:text-monaco-text-0 transition-colors">
                  {language === "de" ? "Datenschutz" : "Privacy Policy"}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-sm">
            <p className="text-monaco-text-2">
              {footerCopyright}
            </p>
            <span className="hidden md:inline text-monaco-text-2">|</span>
            <a 
              href="https://nyza-studio.at" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-monaco-text-2 hover:text-monaco-red transition-colors"
            >
              Webdesign: nyza-studio.at
            </a>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-monaco-red/10 text-monaco-red font-bold text-xs">
              18+
            </span>
            <span className="text-monaco-text-2">
              {language === "de" ? "Nur für Erwachsene" : "Adults Only"}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
